import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { scenariosByPillar } from '@/map/scenario/definitions';

function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s ? `${m}m ${s}s` : `${m} min`;
}

export function ScenarioButton() {
  const [modalOpen, setModalOpen] = useState(false);
  const triggerScenario = useAppStore((s) => s.triggerScenario);
  const activeScenario = useAppStore((s) => s.scenarioState.activeScenario);

  // Hide button when scenario is playing
  if (activeScenario) return null;

  const groups = scenariosByPillar();

  const handlePlay = (id: string) => {
    setModalOpen(false);
    triggerScenario(id);
  };

  return (
    <>
      <motion.button
        onClick={() => setModalOpen(true)}
        className="flex items-center gap-2 px-5 py-3 bg-teal-500/20 border border-teal-400/40 rounded-full text-teal-300 font-medium text-sm backdrop-blur-xl hover:bg-teal-500/30 transition-all"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Play size={16} fill="currentColor" />
        SCENARIOS
      </motion.button>

      {createPortal(
        <AnimatePresence>
          {modalOpen && (
            <motion.div
              className="fixed inset-0 z-[100] flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Backdrop */}
              <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setModalOpen(false)} />

              {/* Modal - same bg as side panels */}
              <motion.div
                className="relative w-[760px] max-h-[82vh] bg-[var(--panel-bg)] backdrop-blur-2xl border border-[var(--border)] rounded-2xl overflow-hidden"
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
                  <div>
                    <h2 className="text-lg font-semibold text-[var(--text-primary)]">Scenarios</h2>
                    <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                      Cinematic demonstrations, grouped by Skewb offering
                    </p>
                  </div>
                  <button
                    onClick={() => setModalOpen(false)}
                    className="p-2 rounded-lg hover:bg-[var(--badge-bg)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Pillar-grouped scenario list */}
                <div className="p-4 space-y-5 overflow-y-auto max-h-[62vh] scrollbar-thin">
                  {groups.map((group) => (
                    <section key={group.pillar}>
                      <h3 className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[var(--text-muted)] mb-2 px-1">
                        {group.label}
                      </h3>
                      <div className="space-y-2">
                        {group.items.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => handlePlay(s.id)}
                            className="w-full text-left p-4 rounded-xl bg-[var(--badge-bg)] border border-[var(--border)] hover:border-teal-400/30 hover:bg-[var(--card-bg)] transition-all group"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.accent }} />
                                <span className="text-[var(--text-primary)] font-medium text-sm">{s.title}</span>
                              </div>
                              <span className="text-xs text-[var(--text-muted)]">{formatDuration(s.durationSec)}</span>
                            </div>
                            <p className="text-xs text-[var(--text-secondary)] mt-1.5 ml-5">{s.summary}</p>
                            <div className="flex items-center gap-1.5 mt-2 ml-5 flex-wrap">
                              {s.productTags.map((tag) => (
                                <span
                                  key={tag}
                                  className="text-[10px] text-[var(--text-muted)] bg-[var(--card-bg)] border border-[var(--border)] px-2 py-0.5 rounded-full"
                                >
                                  {tag}
                                </span>
                              ))}
                              <span className="flex items-center gap-1 ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
                                <Play size={10} className="text-teal-400" fill="currentColor" />
                                <span className="text-[10px] text-teal-400 font-medium">PLAY</span>
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
